'use client';

import React, { createContext, useContext, useState } from 'react';
import { RpcError } from '@dealroadshow/json-rpc-dispatcher';
import noop from 'lodash/noop';
import ErrorCodeHelper from '@dealroadshow/error-codes';
import getRouter from '@users/router/Router';

import { useSessionContext } from '@users/session/SessionContext';
import { AlertManager } from '@dealroadshow/uikit-alert';
import { useMessageManager } from '@framework/message';
import { messageCodes } from '@framework/message/messages';

import UsersSessionRepository, {
  INVESTOR_LOGIN_SSID_KEY,
} from '@users/session/SessionRepository';
import SessionRepository from '@/users/infrastructure/repository/session/SessionRepository';

import SessionStorageRepository from '@framework/browser/storage/SessionStorageRepository';
import { isDataroomTenant } from '@dataroom/tenant/validateDataroomTenant';
import User from '@users/user/User';
import { IUser } from '@users/user/vo/User';
import dmPortalConfig from '@dm-portal/config';
import dealroadshowConfig from '@drs/config';
import evercallConfig from '@evercall/config';
import evercallUrl from '@evercall/url';

import CallbackUrl from '@framework/url/CallbackUrl';

import usersUrl from '@users/url';
import finsightWebUrl from '@/finsight/infrastructure/finsightWebUrl';
import dmPortalUrl from '@dm-portal/url';
import TenantConfig from '@framework/tenant/TenantConfig';
import UserPermission from '@users/user/UserPermission';
import { getContainer } from '@framework/di/getContainer';

export const useLogin = () => {
  const container = getContainer();
  const usersSessionRepository = container.get<UsersSessionRepository>(UsersSessionRepository);
  const sessionRepository = container.get<SessionRepository>(SessionRepository);
  const sessionStorageRepository = container.get<SessionStorageRepository>(SessionStorageRepository);

  const {
    propagate,
    setIsTwoFactorAuthenticationRequired,
    getCurrentUser,
    getSession,
    setSsidCookie,
    session,
    currentUser,
  } = useSessionContext();
  const { getErrorMessage, getMessageByErrorCode, getMessageByName } = useMessageManager();

  const [loggingIn, setLoggingIn] = useState(false);
  const [transferring, setTransferring] = useState(false);

  /**
   * Login user and get ssid
   */
  async function login({
    email,
    password,
  }: {
    email: string,
    password: string,
  }) {
    const callbackUrl = CallbackUrl.fromGetParam() || dmPortalUrl.getUrl();
    const tenant = TenantConfig.validateTenant(new URLSearchParams(window.location.search).get('tenant'))
      || dmPortalConfig.code;
    const SAMLRequest = (new URLSearchParams(window.location.search).get('SAMLRequest'));
    const RelayState = (new URLSearchParams(window.location.search).get('RelayState'));

    setLoggingIn(true);
    try {
      let ssid: string;
      let redirectUrl: string | undefined;
      const payload = { email, password, tenant, callbackUrl };
      switch (true) {
        case isDataroomTenant(tenant):
          ssid = await sessionRepository.createDataroomSession(payload, tenant);
          break;
        default: {
          const result = await usersSessionRepository.createUserSession(payload);
          ssid = result.ssid;
          redirectUrl = result.redirectUrl;
          break;
        }
      }

      await setSsidCookie(ssid);

      setLoggingIn(false);
      onLoginSuccess();

      // OAuth callback (validated same-domain server-side) - skip propagate() entirely.
      if (redirectUrl) {
        // Validate same-domain before redirecting
        try {
          const redirectUrlObj = new URL(redirectUrl, window.location.origin);
          if (redirectUrlObj.origin !== window.location.origin) {
            throw new Error('Cross-origin redirect not allowed from client');
          }
          window.location.href = redirectUrl;
        } catch (error) {
          // Invalid URL or cross-origin - fall back to standard flow
          await afterLogin({ ssid, callbackUrl, SAMLRequest, RelayState });
        }
        return true;
      }

      await afterLogin({ ssid, callbackUrl, SAMLRequest, RelayState });
      return true;
    } catch (e) {
      setLoggingIn(false);
      return onLoginError(e, {
        email,
        callbackUrl: callbackUrl || window.location.href,
        tenant: tenant || TenantConfig.fromHostname().code,
      });
    }
  }

  async function loginInvestor<TData extends object>(
    {
      data,
      createSessionMethod,
      onSuccess,
      onError = noop,
      clearCache = false,
    }: {
      data: TData,
      createSessionMethod(data: TData): Promise<{ ssid: string }>,
      onSuccess({ ssid, user }: { ssid: string, user: IUser }): Promise<void>,
      onError?(e: any, data: TData, defaultOnLoginError: typeof onLoginError): void,
      clearCache?: boolean,
    },
  ) {
    setLoggingIn(true);
    try {
      const { ssid } = await createSessionMethod(data);
      // @ts-ignore
      const user = await usersSessionRepository.getCurrentUser(ssid, { clearCache });
      setLoggingIn(false);

      if (onSuccess) {
        onSuccess({ user, ssid });
      }
      return true;
    } catch (e) {
      setLoggingIn(false);
      if (onError !== noop) {
        return onError(e, data, onLoginError);
      }
      return onLoginError(e, data);
    }
  }

  /**
   * Login DRS investor
   */
  function loginInvestorDrs({
    email,
    entryCode,
    verificationToken,
    videoId,
    source,
  }: {
    email: string,
    entryCode: string,
    verificationToken?: string,
    videoId?: string,
    source?: string,
  }) {
    entryCode = entryCode.trim();
    email = email.toLowerCase();

    return loginInvestor(
      {
        data: {
          tenant: dealroadshowConfig.code,
          email,
          params: {
            entryCode,
            verificationToken,
          },
        },
        createSessionMethod: usersSessionRepository.createCustomLogin,
        onSuccess: async ({ ssid, user }) => {
          if (User.isInvestorProfileFull(user)) {
            await setSsidCookie(ssid);

            const url = new URL(`/e/${ entryCode }`, window.location.origin);
            if (videoId) url.searchParams.set('v', videoId);
            if (source) url.searchParams.set('source', source);

            window.location.href = url.toString();

            return;
          }

          sessionStorageRepository.clear();
          // Should go after on login success, since it clears session storage
          sessionStorageRepository.setItem(INVESTOR_LOGIN_SSID_KEY, ssid);

          const completeProfileUrl = new URL(
            `/login/investor/complete_profile/e/${ entryCode }`,
            window.location.origin,
          );

          if (videoId) completeProfileUrl.searchParams.set('v', videoId);
          if (source) completeProfileUrl.searchParams.set('source', source);

          getRouter().push(completeProfileUrl.toString());
        },
      },
    );
  }

  /**
   * Login Evercall investor
   */
  function loginInvestorEvercall({
    data: {
      email,
      dashboardId,
      token,
    },
    showCompleteProfileForm,
  }: {
    data: {
      email: string,
      dashboardId: string,
      token: string | null,
    },
    showCompleteProfileForm: ({ user }: { user: IUser }) => void,
  }) {
    const corporateEmail = email.toLowerCase();
    const dashboardIdTrimmed = dashboardId.trim();

    return loginInvestor(
      {
        data: {
          tenant: evercallConfig.code,
          email: corporateEmail,
          params: {
            dashboardId: dashboardIdTrimmed,
            token,
          },
        },
        createSessionMethod: usersSessionRepository.createCustomLogin,
        onSuccess: async ({ ssid, user }) => {
          if (User.isInvestorProfileFull(user)) {
            return afterLogin(
              {
                ssid,
                callbackUrl: evercallUrl.getDashboardLoginCallbackUrl(dashboardIdTrimmed),
              },
            );
          }

          sessionStorageRepository.clear();
          // Should go after on login success, since it clears session storage
          sessionStorageRepository.setItem(INVESTOR_LOGIN_SSID_KEY, ssid);
          showCompleteProfileForm({ user });
          return undefined;
        },
        onError: (e, data, defaultOnLoginError) => {
          if (
            e.error.code === ErrorCodeHelper.getCodeByName('EVERCALL_CHAT_INVALID_ARGUMENTS')
            && e.error?.data?.exception === 'SubscriptionDeniedException'
          ) {
            AlertManager.info(getMessageByName(
              messageCodes.EVERCALL_LOGIN_DENIED_DUE_TO_MULTIPLE_OPEN_TABS,
              { corporateEmail: email, dashboardId }),
            );
            throw e;
          } else {
            return defaultOnLoginError(e, data);
          }
        },
      },
    );
  }

  /**
   * Login Evercall OACC investor
   */
  function loginInvestorEvercallOacc(
    {
      data: {
        email,
        conferenceId,
      },
      showCompleteProfileForm,
      onLoginSucceed,
      onError,
    }: {
      data: {
        email: string,
        conferenceId: string | null,
      },
      showCompleteProfileForm: ({ user }: { user: IUser }) => void,
      onLoginSucceed: ({ ssid, user }: { ssid: string, user: IUser }) => Promise<void>,
      onError: (e: any) => void,
    },
  ) {
    const corporateEmail = email.toLowerCase();

    return loginInvestor(
      {
        clearCache: true,
        data: {
          tenant: evercallConfig.code,
          email: corporateEmail,
          params: {
            conferenceId,
          },
        },
        createSessionMethod: usersSessionRepository.createCustomLogin,
        onSuccess: async ({ ssid, user }) => {
          if (User.isInvestorProfileFull(user)) {
            return onLoginSucceed({ ssid, user });
          }

          sessionStorage.clear();
          // Should go after on login success, since it clears session storage
          sessionStorage.setItem(INVESTOR_LOGIN_SSID_KEY, ssid);
          showCompleteProfileForm({ user });
          return undefined;
        },
        onError: (e, data, defaultOnLoginError) => {
          onError(e);
          if (e?.error?.code === ErrorCodeHelper.getCodeByName('EVERCALL_OACC_ACCESS_IS_RESTRICTED')) {
            // This error is shown when the participant registration was denied
            AlertManager.error(getMessageByErrorCode(ErrorCodeHelper.getCodeByName('SESSION_ACCESS_DENIED')));
          } else {
            defaultOnLoginError(e, data);
          }
        },
      },
    );
  }

  async function loginSso({
    email,
  }: {
    email: string,
  }) {
    const callbackUrl = CallbackUrl.fromGetParam() || finsightWebUrl.getUrl();
    const SAMLRequest = (new URLSearchParams(window.location.search).get('SAMLRequest'));
    const RelayState = (new URLSearchParams(window.location.search).get('RelayState'));

    await usersSessionRepository.invalidateSession().catch(() => {});

    window.location.href = usersUrl.getSsoLoginUrl(email, callbackUrl, SAMLRequest, RelayState);
  }

  /**
   * Transfer existing session
   */
  async function transferExistingSession() {
    if (UserPermission.isTwoFactorAuthenticationRequired(session, currentUser)) {
      setIsTwoFactorAuthenticationRequired(true);
      return false;
    }

    setTransferring(true);
    const callbackUrl = CallbackUrl.fromGetParam() || dmPortalUrl.getUrl();
    const SAMLRequest = (new URLSearchParams(window.location.search).get('SAMLRequest'));
    const RelayState = (new URLSearchParams(window.location.search).get('RelayState'));
    const tenant = TenantConfig.validateTenant(new URLSearchParams(window.location.search).get('tenant'))
      || dmPortalConfig.code;
    let sessionValid = false;
    try {
      switch (true) {
        case isDataroomTenant(tenant):
          sessionValid = await sessionRepository.verifyDataroomSession(tenant);
          break;
        default:
          sessionValid = await usersSessionRepository.verifySession();
          break;
      }
    } catch (error) {
      if (error instanceof RpcError) {
        if (error.getCode() === ErrorCodeHelper.getCodeByName('TWO_FACTOR_CHECK_REQUIRED')) {
          setIsTwoFactorAuthenticationRequired(true);
          return false;
        }
      }
      setTransferring(false);
    }

    if (!sessionValid) {
      setTransferring(false);
    }

    return propagate({ callbackUrl, SAMLRequest, RelayState });
  }

  function onLoginError(error: unknown, params = {}) {
    if (
      !(error instanceof RpcError) ||
      (
        error.getCode() !== ErrorCodeHelper.getCodeByName('INVESTOR_VERIFICATION_EMAIL_SENT') &&
        error.getCode() !== ErrorCodeHelper.getCodeByName('USER_ACTIVATION_EMAIL_SENT')
      )
    ) {
      const errorMessage = getErrorMessage(error, params);
      AlertManager.error(errorMessage);
      throw error;
    }
    // Investor verification email auto sent
    // Activation email auto sent
    throw error;
  }

  function onLoginSuccess() {
    sessionStorage.clear();
  }

  async function afterLogin({
    ssid,
    callbackUrl,
    SAMLRequest,
    RelayState,
  }: {
    ssid?: string,
    callbackUrl?: string,
    SAMLRequest?: string,
    RelayState?: string,
  }) {
    const [currentUser, currentSession] = await Promise.all([
      getCurrentUser({ clearCache: true }),
      getSession({ clearCache: true }),
    ]);
    if (UserPermission.isTwoFactorAuthenticationRequired(currentSession, currentUser)) {
      setIsTwoFactorAuthenticationRequired(true);
    } else {
      propagate({ ssid, callbackUrl, SAMLRequest, RelayState });
    }
  }

  return {
    login,
    loginInvestorDrs,
    loginInvestorEvercall,
    loginInvestorEvercallOacc,
    loginSso,
    transferExistingSession,
    transferring,
    loggingIn,
  };
};

export const LoginContext = createContext<ReturnType<typeof useLogin>>(null);

export function useLoginContext() {
  const context = useContext(LoginContext);
  if (!context) {
    throw new Error('useLoginContext must be used within the LoginContext');
  }
  return context;
}

function LoginContextProvider({ children }: React.PropsWithChildren) {
  return (
    <LoginContext.Provider value={ useLogin() }>
      { children }
    </LoginContext.Provider>
  );
}

export default LoginContextProvider;
